import { Building2, CheckCircle2, FileText, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import type {
  CompanyBusinessProfile,
  CompanyContextFact,
  ContextSource,
  ProfileField,
} from './api';

export const profileFields: Array<{
  field: ProfileField;
  label: string;
  placeholder: string;
  list?: boolean;
}> = [
  {
    field: 'description',
    label: 'What the company does',
    placeholder: 'Explain what you do, who you help and the problem you solve.',
  },
  {
    field: 'offerings',
    label: 'Products & services',
    placeholder: 'One product or service per line',
    list: true,
  },
  {
    field: 'customerSegments',
    label: 'Customers',
    placeholder: 'Who buys from you? One customer group per line.',
    list: true,
  },
  {
    field: 'customerNeeds',
    label: 'Customer needs',
    placeholder: 'The problems customers need you to solve',
    list: true,
  },
  {
    field: 'serviceAreas',
    label: 'Markets & locations',
    placeholder: 'Where you operate or serve customers',
    list: true,
  },
  {
    field: 'businessModel',
    label: 'How the business earns revenue',
    placeholder: 'For example, project fees, subscriptions, retail sales or long-term contracts.',
  },
  {
    field: 'channels',
    label: 'Sales & delivery channels',
    placeholder: 'How customers find, buy from and receive your offering',
    list: true,
  },
  {
    field: 'differentiation',
    label: 'What makes you different',
    placeholder: 'Reasons customers choose you',
    list: true,
  },
  {
    field: 'capabilities',
    label: 'Strengths & capabilities',
    placeholder: 'Important expertise, assets, partnerships or resources',
    list: true,
  },
  {
    field: 'proofPoints',
    label: 'Evidence & achievements',
    placeholder: 'Relevant credentials, results, case studies or achievements',
    list: true,
  },
  {
    field: 'existingMission',
    label: 'Existing mission',
    placeholder: 'A mission the company already uses, if one exists.',
  },
  {
    field: 'existingVision',
    label: 'Existing vision',
    placeholder: 'A vision the company already uses, if one exists.',
  },
  {
    field: 'existingValues',
    label: 'Existing values',
    placeholder: 'Values the company already uses, if any',
    list: true,
  },
  {
    field: 'statedGoals',
    label: 'Goals & priorities',
    placeholder: 'Your actual ambitions and current priorities',
    list: true,
  },
  {
    field: 'constraints',
    label: 'Challenges & constraints',
    placeholder: 'Limitations, risks or decisions your strategy needs to account for',
    list: true,
  },
];

export function Provenance({
  field,
  facts = [],
  sources = [],
  value,
}: {
  field: ProfileField;
  facts?: CompanyContextFact[];
  sources?: ContextSource[];
  value?: string | string[];
}) {
  const normalize = (input: string | string[]) =>
    Array.isArray(input)
      ? input
          .map((item) => item.trim())
          .filter(Boolean)
          .join('\n')
      : input.trim();
  if (value !== undefined && !normalize(value))
    return <span className="context-provenance">Not yet known</span>;
  const fieldFacts = facts.filter((fact) => fact.field === field);
  if (!fieldFacts.length) return <span className="context-provenance">Added by you</span>;
  if (value !== undefined && !fieldFacts.some((fact) => normalize(fact.value) === normalize(value)))
    return <span className="context-provenance">Edited by you</span>;
  const sourceIds = new Set(fieldFacts.flatMap((fact) => fact.sourceIds));
  const supportingSources = sources.filter((source) => sourceIds.has(source.id));
  const labels = supportingSources.map((source) => source.label);
  const inferred = fieldFacts.some((fact) => fact.inference);
  return (
    <div className="context-provenance-group">
      <span
        className={`context-provenance ${inferred ? 'is-inferred' : ''}`}
        title={labels.join(' · ')}
      >
        {inferred
          ? 'Suggested · confirm accuracy'
          : labels.length
            ? `From ${labels[0]}${labels.length > 1 ? ` +${labels.length - 1}` : ''}`
            : fieldFacts.some((fact) => fact.origin === 'interview')
              ? 'From your answers'
              : 'Added by you'}
      </span>
      {supportingSources.length > 0 && (
        <details className="context-evidence">
          <summary>View evidence</summary>
          <ul>
            {supportingSources.map((source) => {
              const page = source.metadata?.excerptPage ?? source.metadata?.page;
              return (
                <li key={source.id}>
                  {source.url ? (
                    <a href={source.url} target="_blank" rel="noreferrer">
                      {source.label}
                    </a>
                  ) : (
                    <strong>{source.fileName || source.label}</strong>
                  )}
                  {typeof page === 'number' && <span> · Page {page}</span>}
                  {source.excerpt ? (
                    <blockquote>{source.excerpt}</blockquote>
                  ) : (
                    <p>Review the original source for its supporting details.</p>
                  )}
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </div>
  );
}

export function ProfileEditor({
  profile,
  onChange,
  disabled = false,
  facts,
  sources,
}: {
  profile: CompanyBusinessProfile;
  onChange: (profile: CompanyBusinessProfile) => void;
  disabled?: boolean;
  facts?: CompanyContextFact[];
  sources?: ContextSource[];
}) {
  const update = (field: ProfileField, value: string | string[]) =>
    onChange({ ...profile, [field]: value });
  return (
    <fieldset className="context-profile-fields" disabled={disabled}>
      <div className="context-field-grid">
        <label>
          Company name <span className="context-required">Required</span>
          <input
            aria-label="Company name"
            value={profile.name}
            onChange={(event) => update('name', event.target.value)}
            maxLength={255}
            required
            autoComplete="organization"
            placeholder="Company name"
          />
          <Provenance field="name" value={profile.name} facts={facts} sources={sources} />
        </label>
        <label>
          Industry
          <input
            aria-label="Industry"
            value={profile.industry}
            onChange={(event) => update('industry', event.target.value)}
            maxLength={100}
            placeholder="For example, professional services"
          />
          <Provenance field="industry" value={profile.industry} facts={facts} sources={sources} />
        </label>
        <label>
          Company size
          <select
            aria-label="Company size"
            value={profile.size}
            onChange={(event) => update('size', event.target.value)}
          >
            <option value="">Not yet known</option>
            <option value="startup">1–10 people</option>
            <option value="small">11–50 people</option>
            <option value="medium">51–200 people</option>
            <option value="large">201–1,000 people</option>
            <option value="enterprise">1,000+ people</option>
          </select>
          <Provenance field="size" value={profile.size} facts={facts} sources={sources} />
        </label>
        <label>
          Website
          <input
            aria-label="Website"
            value={profile.website}
            onChange={(event) => update('website', event.target.value)}
            placeholder="https://yourcompany.com"
            inputMode="url"
            maxLength={2048}
          />
        </label>
      </div>
      {profileFields.map(({ field, label, placeholder, list }) => (
        <label key={field}>
          {label} {field === 'description' && <span className="context-required">Required</span>}
          <textarea
            aria-label={label}
            value={list ? (profile[field] as string[]).join('\n') : (profile[field] as string)}
            onChange={(event) =>
              update(field, list ? event.target.value.split('\n') : event.target.value)
            }
            placeholder={placeholder}
            rows={field === 'description' ? 4 : 3}
            maxLength={list || field === 'description' ? 12000 : 4000}
            required={field === 'description'}
          />
          <Provenance field={field} value={profile[field]} facts={facts} sources={sources} />
        </label>
      ))}
    </fieldset>
  );
}

export function BusinessBrief(
  props: Parameters<typeof BusinessBriefContent>[0] & { collapsible?: boolean }
) {
  const [mobile, setMobile] = useState(
    () =>
      typeof window !== 'undefined' &&
      (window.matchMedia?.('(max-width: 850px)').matches ?? window.innerWidth <= 850)
  );
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!props.collapsible || !window.matchMedia) return;
    const query = window.matchMedia('(max-width: 850px)');
    const update = () => setMobile(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, [props.collapsible]);
  if (!props.collapsible) return <BusinessBriefContent {...props} />;
  return (
    <div className="context-brief-container">
      <details
        className="context-mobile-brief"
        open={!mobile || expanded}
        onToggle={(event) => {
          if (mobile) setExpanded(event.currentTarget.open);
        }}
      >
        <summary className="context-brief-disclosure">
          <span className="context-icon">
            <Building2 size={19} />
          </span>
          <span>
            <strong>{expanded ? 'Hide company brief' : 'Show company brief'}</strong>
            <small>{props.profile.name || 'Your company understanding'}</small>
          </span>
        </summary>
        <BusinessBriefContent {...props} />
      </details>
    </div>
  );
}

function BusinessBriefContent({
  profile,
  sources,
  onChange,
  facts,
  editable = true,
  reviewState = 'draft',
}: {
  profile: CompanyBusinessProfile;
  sources: ContextSource[];
  facts?: CompanyContextFact[];
  onChange?: (profile: CompanyBusinessProfile) => void;
  editable?: boolean;
  reviewState?: 'draft' | 'saved';
}) {
  const [editing, setEditing] = useState(false);
  const hasContext =
    profile.description || profile.offerings.length || profile.customerSegments.length;
  return (
    <aside className="context-live-brief" aria-label="Company brief">
      <div className="context-brief-title">
        <span className="context-icon">
          <Building2 size={19} />
        </span>
        <div>
          <p className="context-eyebrow">Your company brief</p>
          <h2>{profile.name || 'Taking shape'}</h2>
        </div>
      </div>
      <p className="context-brief-caption">
        The shared understanding behind your future strategy and AI suggestions.
      </p>
      {editable && onChange && (
        <button type="button" className="context-text-button" onClick={() => setEditing(!editing)}>
          {editing ? 'Close editor' : 'Edit company brief'}
        </button>
      )}
      {editing && onChange ? (
        <ProfileEditor
          profile={profile}
          onChange={onChange}
          disabled={!editable}
          facts={facts}
          sources={sources}
        />
      ) : (
        <>
          <div className="context-brief-description">
            {profile.description || 'Add a website, documents or your own words to begin.'}
          </div>
          <Provenance
            field="description"
            value={profile.description}
            facts={facts}
            sources={sources}
          />
          {profile.industry && (
            <p className="context-brief-industry">
              {profile.industry}
              {profile.size ? ` · ${profile.size}` : ''}
            </p>
          )}
          {profileFields
            .filter(({ field }) => field !== 'description')
            .map(({ field, label, list }) => {
              const value = profile[field];
              const values = list
                ? (value as string[]).filter(Boolean)
                : value
                  ? [value as string]
                  : [];
              return values.length ? (
                <section className="context-brief-section" key={field}>
                  <h3>{label}</h3>
                  <ul>
                    {values.slice(0, 5).map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                  <Provenance
                    field={field}
                    value={profile[field]}
                    facts={facts}
                    sources={sources}
                  />
                </section>
              ) : null;
            })}
          {!hasContext && (
            <div className="context-brief-empty">
              <Sparkles size={24} />
              <p>As we learn about your company, this brief fills in alongside you.</p>
            </div>
          )}
        </>
      )}
      <div className="context-brief-sources">
        <FileText size={15} /> {sources.filter((source) => source.status !== 'deleted').length}{' '}
        sources added
        {(hasContext || reviewState === 'saved') && (
          <span>
            <CheckCircle2 size={14} />{' '}
            {reviewState === 'saved' ? 'Saved company brief' : 'Ready to review'}
          </span>
        )}
      </div>
    </aside>
  );
}
